import React, { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";

import Navbar from "./components/navbar";
import Hero from "./components/hero";
import TestimonialsSection from "./components/testemunhos";
import About from "./components/about";
import Partners from "./components/parceiros";
import Footer from "./components/footer";
import PrivacyPolicy from "./pages/PP";
import CookiesPolicy from "./pages/PC";
import LegalWarning from "./pages/LW";
import InnovationVoucher from "./pages/PRR";
import Coaching40 from "./pages/Coaching40";
import NotFound from "./pages/NotFound";
import Podcast from "./pages/Podcast";
import { classifyLink, trackEvent, trackPageView } from "./lib/analytics";

function Landing({ lang, onLanguageChange }) {
  return (
    <>
      <Navbar activeLang={lang} onLanguageChange={onLanguageChange} />
      <Hero lang={lang} />
      <TestimonialsSection lang={lang} />
      <About lang={lang} />
      <Partners lang={lang} />
      <Footer lang={lang} />
    </>
  );
}

function LocaleLanding({ forcedLang, lang, syncLang, onLanguageChange }) {
  useEffect(() => {
    if (forcedLang && forcedLang !== lang) {
      syncLang(forcedLang);
    }
  }, [forcedLang, lang, syncLang]);

  return <Landing lang={lang} onLanguageChange={onLanguageChange} />;
}

function LocaleInnovationVoucher({ forcedLang, lang, syncLang, onLanguageChange }) {
  useEffect(() => {
    if (forcedLang && forcedLang !== lang) {
      syncLang(forcedLang);
    }
  }, [forcedLang, lang, syncLang]);

  return <InnovationVoucher lang={lang} onLanguageChange={onLanguageChange} />;
}

function LocaleCoaching40({ forcedLang, lang, syncLang, onLanguageChange }) {
  useEffect(() => {
    if (forcedLang && forcedLang !== lang) {
      syncLang(forcedLang);
    }
  }, [forcedLang, lang, syncLang]);

  return <Coaching40 lang={lang} onLanguageChange={onLanguageChange} />;
}

function LocalePodcast({ forcedLang, lang, syncLang }) {
  useEffect(() => {
    if (forcedLang && forcedLang !== lang) {
      syncLang(forcedLang);
    }
  }, [forcedLang, lang, syncLang]);

  return <Podcast lang={lang} />;
}

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem("language") || "pt");
  const location = useLocation();
  const navigate = useNavigate();
  const lastTrackedPathRef = useRef("");

  useEffect(() => {
    localStorage.setItem("language", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    const pageType = location.pathname === "/404" ? "not_found" : "standard";

    if (lastTrackedPathRef.current === pagePath) {
      return;
    }

    lastTrackedPathRef.current = pagePath;

    trackPageView({
      path: pagePath,
      title: document.title,
      location: window.location.href,
      language: lang,
      pageType,
    });
  }, [lang, location]);

  useEffect(() => {
    const handleTrackedClick = (event) => {
      const link = event.target.closest("a[href]");

      if (!link) {
        return;
      }

      const href = link.getAttribute("href");

      if (!href || href.startsWith("#")) {
        return;
      }

      let targetUrl;

      try {
        targetUrl = new URL(href, window.location.href);
      } catch {
        return;
      }

      const linkType = classifyLink(targetUrl);

      if (!linkType) {
        return;
      }

      trackEvent("click_link", {
        link_type: linkType,
        link_text: (link.textContent || "").trim().slice(0, 100),
        link_url: targetUrl.href,
        page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        language: lang,
      });
    };

    document.addEventListener("click", handleTrackedClick);

    return () => {
      document.removeEventListener("click", handleTrackedClick);
    };
  }, [lang]);

  const syncLangFromRoute = useCallback((value) => {
    setLang((prev) => (prev === value ? prev : value));
  }, []);

  const handleLanguageChange = useCallback(
    (nextLang) => {
      trackEvent("language_change", {
        previous_language: lang,
        new_language: nextLang,
        page_path: `${location.pathname}${location.search}${location.hash}`,
      });

      setLang((prev) => (prev === nextLang ? prev : nextLang));
      const segments = location.pathname.split("/").filter(Boolean);

      const hasPRRSegment = segments.includes("PRR");
      const hasCoachingSegment = segments.some(s => s.toLowerCase() === "coaching4.0");
      const hasPodcastSegment = segments.some(s => s.toLowerCase() === "podcast");

      if (hasPRRSegment) {
        if (segments[0] === "pt" || segments[0] === "en") {
          const nextSegments = [nextLang, ...segments.slice(1)];
          navigate(`/${nextSegments.join("/")}`, { replace: false });
        } else {
          navigate(`/${nextLang}/PRR`, { replace: false });
        }
        return;
      }

      if (hasCoachingSegment) {
        if (segments[0] === "pt" || segments[0] === "en") {
          const nextSegments = [nextLang, ...segments.slice(1)];
          navigate(`/${nextSegments.join("/")}`, { replace: false });
        } else {
          navigate(`/${nextLang}/coaching4.0`, { replace: false });
        }
        return;
      }

      if (hasPodcastSegment) {
        if (segments[0] === "pt" || segments[0] === "en" || segments[0] === "es") {
          const nextSegments = [nextLang, ...segments.slice(1)];
          navigate(`/${nextSegments.join("/")}`, { replace: false });
        } else {
          navigate(`/${nextLang}/podcast`, { replace: false });
        }
        return;
      }

      if (segments.length === 0) {
        navigate(`/${nextLang}`, { replace: false });
        return;
      }

      if (segments[0] === "pt" || segments[0] === "en") {
        if (segments[0] !== nextLang) {
          segments[0] = nextLang;
          navigate(`/${segments.join("/")}`, { replace: false });
        }
      } else {
        navigate(`/${nextLang}`, { replace: false });
      }
    },
    [lang, location.hash, location.pathname, location.search, navigate]
  );

  return (
    <Routes>
      <Route path="/" element={<Landing lang={lang} onLanguageChange={handleLanguageChange} />} />
      <Route path="/PRR" element={<InnovationVoucher lang={lang} onLanguageChange={handleLanguageChange} />} />
      <Route path="/PPR" element={<Navigate to="/PRR" replace />} />
      <Route
        path="/pt"
        element={
          <LocaleLanding
            forcedLang="pt"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      <Route
        path="/pt/PRR"
        element={
          <LocaleInnovationVoucher
            forcedLang="pt"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      {/* redirect lowercase /ppr and wrong /PPR to the correct /PRR */}
      <Route path="/pt/ppr" element={<Navigate to="/pt/PRR" replace />} />
      <Route path="/pt/PPR" element={<Navigate to="/pt/PRR" replace />} />

      <Route
        path="/en"
        element={
          <LocaleLanding
            forcedLang="en"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/en/PRR"
        element={
          <LocaleInnovationVoucher
            forcedLang="en"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      {/* redirect lowercase /ppr and wrong /PPR to the correct /PRR */}
      <Route path="/en/ppr" element={<Navigate to="/en/PRR" replace />} />
      <Route path="/en/PPR" element={<Navigate to="/en/PRR" replace />} />


      {/* Invisible Side Podcast */}
      <Route path="/podcast" element={<Podcast lang={lang} />} />
      <Route
        path="/pt/podcast"
        element={<LocalePodcast forcedLang="pt" lang={lang} syncLang={syncLangFromRoute} />}
      />
      <Route
        path="/en/podcast"
        element={<LocalePodcast forcedLang="en" lang={lang} syncLang={syncLangFromRoute} />}
      />
      <Route
        path="/es/podcast"
        element={<LocalePodcast forcedLang="es" lang={lang} syncLang={syncLangFromRoute} />}
      />

      {/* Política de Privacidade */}
      <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
      <Route path="/politica-cookies" element={<CookiesPolicy />} />
      <Route path="/aviso-legal" element={<LegalWarning />} />

      {/* Coaching 4.0 */}
      <Route path="/coaching4.0" element={<Coaching40 lang={lang} onLanguageChange={handleLanguageChange} />} />
      <Route
        path="/pt/coaching4.0"
        element={
          <LocaleCoaching40
            forcedLang="pt"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/en/coaching4.0"
        element={
          <LocaleCoaching40
            forcedLang="en"
            lang={lang}
            syncLang={syncLangFromRoute}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      {/* 404 — catch-all */}
      <Route
        path="*"
        element={<NotFound lang={lang} onLanguageChange={handleLanguageChange} />}
      />
    </Routes>
  );
}
